/**
 * Learn Art History category grid — client cache (Instagram-style):
 * show last-known cards + totals immediately, revalidate in background.
 */
import { createCacheKey, getCache, setCache, clearCache } from './cache';
import { resolveImageUrl } from './urlResolver';

export const SELECTED_LEVEL_KEY = 'learn-art-history:selected-level';
export const TOTALS_CACHE_PREFIX = 'learn-art-history:totals:v1:';
export const LEVEL_PREVIEW_CACHE_KEY = 'learn-art-history:level-previews:v1';
export const COMPLETED_CACHE_PREFIX = 'learn-art-history:completed:v1:';
export const REVALIDATE_GAP_MS = 60 * 1000;

const CARD_SNAPSHOT_PREFIX = 'learn-art-history:cards:l';
const BUNDLE_CACHE_PREFIX = 'learn-art-history:bundle:v1:';

export const stableCardUrlByLevelKey = new Map();

export function cardSnapshotKey(level) {
  return `${CARD_SNAPSHOT_PREFIX}${level}`;
}

export function stableCardKey(level, cardKey) {
  return `${level}:${cardKey}`;
}

export function isStableVerifiedPreviewUrl(url = '') {
  const u = String(url || '').trim();
  if (/^\/api\/artwork-preview\/[^/?#]+\.(?:webp|jpg|jpeg|png)$/i.test(u)) return true;
  if (/^https?:\/\/[^/]+\/api\/(?:api\/)?artwork-preview\/[^/?#]+\.(?:webp|jpg|jpeg|png)$/i.test(u)) return true;
  if (!/^https?:\/\//i.test(u)) return false;
  const isStorageUrl =
    u.includes('firebasestorage.googleapis.com') || u.includes('storage.googleapis.com');
  if (!isStorageUrl) return false;
  return u.includes('verified-artworks%2F') || u.includes('/verified-artworks/');
}

export function sanitizeSnapshotRows(rows = []) {
  if (!Array.isArray(rows)) return [];
  return rows
    .map((r) => ({
      key: String(r?.key || '').trim(),
      imageUrl: String(r?.imageUrl || '').trim(),
    }))
    .filter((r) => r.key && isStableVerifiedPreviewUrl(r.imageUrl));
}

export function readCardSnapshot(level) {
  const key = cardSnapshotKey(level);
  try {
    const fromLocal = localStorage.getItem(key);
    if (fromLocal) {
      const parsed = sanitizeSnapshotRows(JSON.parse(fromLocal));
      return parsed;
    }
  } catch (_) {}
  try {
    const fromSession = sessionStorage.getItem(key);
    if (fromSession) {
      return sanitizeSnapshotRows(JSON.parse(fromSession));
    }
  } catch (_) {}
  return null;
}

export function writeCardSnapshot(level, value) {
  const safeValue = sanitizeSnapshotRows(value);
  try {
    safeValue.forEach((item) => {
      const k = item?.key;
      const url = item?.imageUrl;
      if (k && typeof url === 'string' && isStableVerifiedPreviewUrl(url)) {
        stableCardUrlByLevelKey.set(stableCardKey(level, k), url);
      }
    });
  } catch (_) {}
  const key = cardSnapshotKey(level);
  try {
    localStorage.setItem(key, JSON.stringify(safeValue));
  } catch (_) {}
  try {
    sessionStorage.setItem(key, JSON.stringify(safeValue));
  } catch (_) {}
}

export function readStoredLevel() {
  try {
    const s = sessionStorage.getItem(SELECTED_LEVEL_KEY);
    const n = parseInt(s, 10);
    if (n >= 1 && n <= 3) return n;
  } catch (_) {}
  return 1;
}

export function writeStoredLevel(level) {
  try {
    sessionStorage.setItem(SELECTED_LEVEL_KEY, String(level));
  } catch (_) {}
}

export function readTotalsCache(level) {
  try {
    const raw = sessionStorage.getItem(TOTALS_CACHE_PREFIX + String(level));
    if (!raw) return {};
    const p = JSON.parse(raw);
    return p && typeof p === 'object' ? p : {};
  } catch (_) {
    return {};
  }
}

export function writeTotalsCache(level, totals) {
  try {
    sessionStorage.setItem(TOTALS_CACHE_PREFIX + String(level), JSON.stringify(totals || {}));
  } catch (_) {}
  try {
    setCache(createCacheKey('learnArtHistoryTotals', { level }, String(level)), totals, 10 * 60 * 1000);
  } catch (_) {}
}

export function readLevelPreviewCache() {
  try {
    const raw = localStorage.getItem(LEVEL_PREVIEW_CACHE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (_) {
    return {};
  }
}

export function writeLevelPreviewCache(value) {
  try {
    const payload = value && typeof value === 'object' ? value : {};
    localStorage.setItem(LEVEL_PREVIEW_CACHE_KEY, JSON.stringify(payload));
  } catch (_) {}
}

export function readCompletedCache(uid, level) {
  if (!uid) return {};
  try {
    const raw = sessionStorage.getItem(`${COMPLETED_CACHE_PREFIX}${uid}:l${level}`);
    if (!raw) return {};
    const p = JSON.parse(raw);
    return p && typeof p === 'object' ? p : {};
  } catch (_) {
    return {};
  }
}

export function writeCompletedCache(uid, level, map) {
  if (!uid) return;
  try {
    sessionStorage.setItem(`${COMPLETED_CACHE_PREFIX}${uid}:l${level}`, JSON.stringify(map || {}));
  } catch (_) {}
}

function bundleKey(level) {
  return `${BUNDLE_CACHE_PREFIX}${level}`;
}

export function readLevelBundle(level) {
  try {
    const raw = sessionStorage.getItem(bundleKey(level));
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (_) {
    return null;
  }
}

export function writeLevelBundle(level, bundle) {
  try {
    sessionStorage.setItem(
      bundleKey(level),
      JSON.stringify({ ...bundle, cachedAt: Date.now() })
    );
  } catch (_) {}
}

export function shouldRevalidateLevel(level) {
  const bundle = readLevelBundle(level);
  if (!bundle?.cachedAt) return true;
  return Date.now() - bundle.cachedAt > REVALIDATE_GAP_MS;
}

/** Build card list with cached image URLs for instant first paint. */
export function hydrateCardsForLevel(level, defaultCards, levelPreviewMap = {}, configImageUrls = {}) {
  const saved = readCardSnapshot(level);
  const previewForLevel = levelPreviewMap?.[String(level)] || levelPreviewMap?.[level] || {};

  return defaultCards.map((card) => {
    const stable = stableCardUrlByLevelKey.get(stableCardKey(level, card.key));
    if (stable) return { ...card, imageUrl: stable };

    const configUrl = configImageUrls[card.key];
    if (configUrl && isStableVerifiedPreviewUrl(configUrl)) {
      return { ...card, imageUrl: resolveImageUrl(configUrl) || configUrl };
    }

    const preview = String(previewForLevel[card.key] || '').trim();
    if (isStableVerifiedPreviewUrl(preview)) {
      return { ...card, imageUrl: resolveImageUrl(preview) || preview };
    }

    const match = Array.isArray(saved) ? saved.find((s) => s.key === card.key) : null;
    if (match?.imageUrl && isStableVerifiedPreviewUrl(match.imageUrl)) {
      return { ...card, imageUrl: resolveImageUrl(match.imageUrl) || match.imageUrl };
    }

    return card;
  });
}

export function warmCategoryCardImages(cards = [], limit = 16) {
  if (typeof window === 'undefined') return;
  cards
    .slice(0, limit)
    .map((c) => c.imageUrl)
    .filter((u) => typeof u === 'string' && /^https?:\/\//i.test(u))
    .forEach((url) => {
      try {
        const img = new Image();
        img.decoding = 'async';
        img.fetchPriority = 'low';
        img.src = url;
      } catch (_) {}
    });
}

/** Call before navigating to Learn Art History so card images are already warming. */
export function warmLearnArtHistoryLanding() {
  if (typeof window === 'undefined') return;
  const level = readStoredLevel();
  const saved = readCardSnapshot(level);
  const previewMap = readLevelPreviewCache()?.[String(level)] || readLevelPreviewCache()?.[level] || {};
  const urls = [];
  if (Array.isArray(saved)) {
    saved.forEach((row) => {
      const u = row?.imageUrl;
      if (u && isStableVerifiedPreviewUrl(u)) urls.push(u);
    });
  }
  Object.values(previewMap || {}).forEach((raw) => {
    const u = String(raw || '').trim();
    if (isStableVerifiedPreviewUrl(u)) urls.push(u);
  });
  warmCategoryCardImages(
    Array.from(new Set(urls)).map((imageUrl) => ({ imageUrl })),
    16
  );
}

export function invalidateLearnArtHistoryCache(uid) {
  try {
    for (let level = 1; level <= 3; level += 1) {
      sessionStorage.removeItem(bundleKey(level));
      if (uid) sessionStorage.removeItem(`${COMPLETED_CACHE_PREFIX}${uid}:l${level}`);
      clearCache(createCacheKey('learnArtHistoryTotals', { level }, String(level)));
    }
  } catch (_) {}
}
